// DOMAIN REGISTRY - Single source of truth for all domain names

export const DOMAINS = {
  GAME: {
    id: "GAME" as const,
    name: "Game Loop",
    description: "Main game loop, tick management, state coordination",
    layer: "core" as const,
  },
  PHYSICS: {
    id: "PHYSICS" as const,
    name: "Physics Engine",
    description: "Movement, rotation, collision detection, spatial grid",
    layer: "core" as const,
  },
  NETWORKING: {
    id: "NETWORKING" as const,
    name: "Network Layer",
    description:
      "WebSocket communication, message broadcasting, delta compression",
    layer: "core" as const,
  },
  PLAYER: {
    id: "PLAYER" as const,
    name: "Player Management",
    description: "Player state, health, elimination, respawn",
    layer: "entity" as const,
  },
  BOT: {
    id: "BOT" as const,
    name: "Bot AI",
    description: "AI-controlled players, pathfinding, targeting",
    layer: "entity" as const,
  },
  ASTEROIDS: {
    id: "ASTEROIDS" as const,
    name: "Asteroids",
    description: "Asteroid spawning, movement, breakup",
    layer: "entity" as const,
  },
  PELLETS: {
    id: "PELLETS" as const,
    name: "Pellets",
    description: "Pellet spawning, collection, magnetic attraction",
    layer: "entity" as const,
  },
  MINES: {
    id: "MINES" as const,
    name: "Mines",
    description: "Mine deployment, explosion, damage",
    layer: "entity" as const,
  },
  BULLETS: {
    id: "BULLETS" as const,
    name: "Bullets",
    description: "Bullet firing, movement, collision",
    layer: "entity" as const,
  },
  COLLISION: {
    id: "COLLISION" as const,
    name: "Collision System",
    description: "Collision detection, resolution, damage application",
    layer: "system" as const,
  },
  LOBBY: {
    id: "LOBBY" as const,
    name: "Lobby System",
    description: "Matchmaking, countdown, game start/end",
    layer: "system" as const,
  },
  QUEUE: {
    id: "QUEUE" as const,
    name: "Queue Management",
    description: "Player queue, lobby assignment",
    layer: "system" as const,
  },
  DATABASE: {
    id: "DATABASE" as const,
    name: "Database",
    description: "Persistence, queries, transactions",
    layer: "system" as const,
  },
  AUTH: {
    id: "AUTH" as const,
    name: "Authentication",
    description: "User auth, session management, better-auth",
    layer: "system" as const,
  },
  METRICS: {
    id: "METRICS" as const,
    name: "Metrics",
    description: "Performance metrics, telemetry",
    layer: "system" as const,
  },
  STATS: {
    id: "STATS" as const,
    name: "Stats Tracking",
    description: "Player stats, leaderboard",
    layer: "system" as const,
  },
  ROUTES: {
    id: "ROUTES" as const,
    name: "HTTP Routes",
    description: "API endpoints, HTTP handlers",
    layer: "infrastructure" as const,
  },
  BUG: {
    id: "BUG" as const,
    name: "Bug Reporting",
    description: "Bug capture, error reporting",
    layer: "infrastructure" as const,
  },
  OG: {
    id: "OG" as const,
    name: "OG Image Generation",
    description: "Dynamic OG image creation",
    layer: "infrastructure" as const,
  },
  STARTUP: {
    id: "STARTUP" as const,
    name: "Server Startup",
    description: "Initialization, bootstrap, health checks",
    layer: "infrastructure" as const,
  },
  RENDERING: {
    id: "RENDERING" as const,
    name: "Rendering Engine",
    description: "Canvas rendering, draw calls, frame management",
    layer: "client" as const,
  },
  EFFECTS: {
    id: "EFFECTS" as const,
    name: "Visual Effects",
    description: "Particles, explosions, bloom, damage numbers",
    layer: "client" as const,
  },
  THEME: {
    id: "THEME" as const,
    name: "Theme System",
    description: "Color palette, visual styling",
    layer: "client" as const,
  },
  INPUT: {
    id: "INPUT" as const,
    name: "Input System",
    description: "Keyboard, mouse, touch input capture",
    layer: "client" as const,
  },
  UI: {
    id: "UI" as const,
    name: "UI System",
    description: "React components, HUD, overlays",
    layer: "client" as const,
  },
  AUDIO: {
    id: "AUDIO" as const,
    name: "Audio System",
    description: "Sound effects, music, spatial audio",
    layer: "client" as const,
  },
  WEBSOCKET: {
    id: "WEBSOCKET" as const,
    name: "WebSocket Client",
    description: "Client-side WebSocket, reconnection, message handling",
    layer: "client" as const,
  },
  LOBBY_CLIENT: {
    id: "LOBBY_CLIENT" as const,
    name: "Lobby UI",
    description: "Client-side lobby rendering, countdown",
    layer: "client" as const,
  },
  CLIENT: {
    id: "CLIENT" as const,
    name: "Client Core",
    description: "Client initialization, lifecycle management",
    layer: "client" as const,
  },
  ECS: {
    id: "ECS" as const,
    name: "ECS System",
    description: "State reducer, effect queue",
    layer: "client" as const,
  },
  REACT: {
    id: "REACT" as const,
    name: "React Runtime",
    description: "React component lifecycle, hooks",
    layer: "client" as const,
  },
  MODALS: {
    id: "MODALS" as const,
    name: "Modal System",
    description: "Modal dialogs, overlays",
    layer: "client" as const,
  },
  TOAST: {
    id: "TOAST" as const,
    name: "Toast Notifications",
    description: "Toast messages, alerts",
    layer: "client" as const,
  },
  PERF: {
    id: "PERF" as const,
    name: "Performance Monitor",
    description: "FPS, frame time, performance dashboard",
    layer: "client" as const,
  },
} as const;

export type DomainLayer =
  | "core"
  | "entity"
  | "system"
  | "infrastructure"
  | "client";

export interface DomainDefinition {
  readonly id: string;
  readonly name: string;
  readonly description: string;
  readonly layer: DomainLayer;
}

export type DomainId = (typeof DOMAINS)[keyof typeof DOMAINS]["id"];

export type Domain = keyof typeof DOMAINS;

export function getDomain(id: DomainId): DomainDefinition {
  const domain = Object.values(DOMAINS).find((d) => d.id === id);
  if (!domain) {
    throw new Error(`Domain "${id}" not found in registry`);
  }
  return domain;
}

export function getDomainsByLayer(layer: DomainLayer): DomainDefinition[] {
  return Object.values(DOMAINS).filter((d) => d.layer === layer);
}

export function isDomainId(value: string): value is DomainId {
  return Object.values(DOMAINS).some((d) => d.id === value);
}

export const ALL_DOMAIN_IDS = Object.values(DOMAINS).map((d) => d.id);

export const ALL_DOMAINS = Object.keys(DOMAINS) as Domain[];
